export default function TransportInstallation() {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 w-full">
      <div className="max-w-7xl mx-auto w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-thin mb-4 sm:mb-6 px-4">
            Transport & <span className="text-gray-300">Installation</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            Seamless delivery and rapid deployment process
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16 lg:mb-20 w-full">
          {[
            {
              step: '01',
              title: 'Factory Production',
              description: 'Precision manufacturing with quality control',
              icon: 'ri-factory-line',
              image: 'modern factory production line manufacturing modular office cabins with quality control precision engineering industrial facility clean environment',
              customImage: '/images/714ddbf3d73a.webp'
            },
            {
              step: '02',
              title: 'Secure Transport',
              description: 'Professional logistics and delivery',
              icon: 'ri-truck-line',
              image: 'professional logistics truck transporting modular office cabin on flatbed trailer secure delivery modern transportation highway scene',
              customImage: '/images/848cb2d61e89.webp'
            },
            {
              step: '03',
              title: 'Site Preparation',
              description: 'Foundation and utility connections',
              icon: 'ri-tools-line',
              image: 'construction site preparation for modular office cabin foundation work utility connections professional installation team modern equipment',
              customImage: '/images/13e02122b557.webp'
            },
            {
              step: '04',
              title: 'Final Installation',
              description: 'Complete setup in 24-48 hours',
              icon: 'ri-checkbox-circle-line',
              image: 'final installation of modular office cabin with crane professional installation team completed setup modern workspace ready to use',
              customImage: '/images/da167ee980aa.webp'
            }
          ].map((step, index) => (
            <div key={index} className="group relative bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl overflow-hidden hover:border-gray-500/30 hover:shadow-[0_0_30px_rgba(148,163,184,0.2)] transition-all duration-500 w-full">
              {/* Image */}
              <div className="relative w-full h-40 sm:h-48 overflow-hidden">
                <img 
                  src={step.customImage || `/images/ai-step-image-25ab47.webp`}
                  alt={step.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Step Number */}
                <div className="absolute top-4 right-4 w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/20">
                  <span className="text-base sm:text-lg font-bold text-gray-300">{step.step}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4">
                  <i className={`${step.icon} text-lg sm:text-xl text-gray-300`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-medium text-white mb-2 break-words">{step.title}</h3>
                <p className="text-xs sm:text-sm text-gray-400 break-words">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Installation Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 mb-12 sm:mb-16 w-full">
          {/* Timeline */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 w-full">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-medium text-white mb-6 sm:mb-8 break-words">Installation Timeline</h3>
            <div className="space-y-4 sm:space-y-6 w-full">
              {[
                { time: 'Day 1', task: 'Site preparation and foundation work', duration: '4-6 hours' },
                { time: 'Day 1-2', task: 'Cabin delivery and positioning', duration: '2-3 hours' },
                { time: 'Day 2', task: 'Utility connections and testing', duration: '3-4 hours' },
                { time: 'Day 2', task: 'Interior finishing and inspection', duration: '2-3 hours' }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 pb-4 sm:pb-6 border-b border-white/10 last:border-0 w-full">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xs sm:text-sm font-bold text-gray-300">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2 mb-1 sm:mb-2">
                      <span className="text-sm sm:text-base font-medium text-white break-words">{item.time}</span>
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">{item.duration}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 break-words">{item.task}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 w-full">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-medium text-white mb-6 sm:mb-8 break-words">Site Requirements</h3>
            <div className="space-y-4 sm:space-y-6 w-full">
              {[
                { icon: 'ri-ruler-line', title: 'Space Requirements', desc: 'Level ground with adequate clearance' },
                { icon: 'ri-plug-line', title: 'Power Supply', desc: 'Standard electrical connection (220V/380V)' },
                { icon: 'ri-water-flash-line', title: 'Utilities Access', desc: 'Water and drainage connections if needed' },
                { icon: 'ri-road-map-line', title: 'Access Route', desc: 'Clear path for delivery truck (min 3.5m wide)' }
              ].map((req, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 w-full">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-500/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${req.icon} text-lg sm:text-xl text-gray-300`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm sm:text-base font-medium text-white mb-1 break-words">{req.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-400 break-words">{req.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center px-4 w-full">
          <p className="text-sm sm:text-base lg:text-lg text-gray-400 mb-6 sm:mb-8">
            Need help with site assessment or installation planning?
          </p>
          <a 
            href="/contact"
            className="inline-block w-full sm:w-auto px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-gray-300 text-black text-sm sm:text-base font-medium rounded-full hover:bg-gray-200 transition-all hover:shadow-[0_0_30px_rgba(148,163,184,0.6)] whitespace-nowrap cursor-pointer"
          >
            Schedule Site Consultation
          </a>
        </div>
      </div>
    </section>
  );
}
